import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * AgencyInviteAccept (`/agency-invite/:token`) — public landing page for a
 * broker-issued email invite. Shows the agency name + trader's own name,
 * asks for phone + a password they choose, then activates their account.
 *
 * Auto-logs the client in on success and hands off to the trading terminal.
 */
export default function AgencyInviteAccept() {
    const { token } = useParams();
    const nav = useNavigate();
    const { login } = useAuth();
    const [info, setInfo] = useState({ state: "loading" });
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get(`/agency/invite-info/${token}`);
                setInfo({ state: "ok", data: r.data });
            } catch (err) {
                setInfo({
                    state: "err",
                    detail: err?.response?.data?.detail || "Invite invalid or expired",
                });
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/agency/accept-invite", {
                invite_token: token, phone, password,
            });
            toast.success("Account activated. Signing you in…");
            try {
                await login(info.data.email, password);
                nav("/app/terminal");
            } catch {
                nav("/login");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Activation failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
            <div className="w-full max-w-md border border-border bg-card rounded-lg p-6"
                 data-testid="agency-invite-accept">
                {info.state === "loading" && (
                    <div className="text-center py-6">
                        <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin mb-3" />
                        <p className="text-sm text-muted-foreground">Verifying invite…</p>
                    </div>
                )}
                {info.state === "err" && (
                    <div className="text-center py-6">
                        <XCircle className="w-10 h-10 mx-auto text-danger mb-3" />
                        <h1 className="font-bold text-lg mb-1">Invite unavailable</h1>
                        <p className="text-xs text-muted-foreground">{info.detail}</p>
                    </div>
                )}
                {info.state === "ok" && (
                    <>
                        <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">
                            Invitation from {info.data.agency_name}
                        </div>
                        <h1 className="text-2xl font-extrabold tracking-tight mb-1">
                            Welcome, {info.data.full_name}
                        </h1>
                        <p className="text-xs text-muted-foreground mb-4">
                            Set your phone number and a password to activate your Melian Dialogue account.
                            Institutional access · zero client-side fees.
                        </p>
                        <form onSubmit={submit} className="space-y-3">
                            <F label="Phone (+254…)" value={phone} onChange={setPhone}
                               testId="invite-phone" />
                            <F label="Choose a password (min 8 characters)" type="password"
                               value={password} onChange={setPassword} testId="invite-password" />
                            <button
                                type="submit"
                                disabled={busy || !phone || !password}
                                data-testid="invite-submit"
                                className="w-full py-2.5 mt-2 rounded bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60"
                            >
                                {busy ? "Activating…" : (
                                    <span className="inline-flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4" />Activate my account
                                    </span>
                                )}
                            </button>
                        </form>
                        <p className="text-[10px] text-muted-foreground mt-4 text-center">
                            <KeyRound className="w-3 h-3 inline mr-1" />
                            Your broker cannot see this password.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

function F({ label, value, onChange, type = "text", testId }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-muted-foreground mb-1">{label}</span>
            <input
                type={type}
                required
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded focus:border-primary focus:outline-none"
                autoComplete={type === "password" ? "new-password" : "off"}
            />
        </label>
    );
}
